export function createTimeline(frames, canvas) {
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true })

    // Tunables (safe defaults)
    const WINDOW_RADIUS = 12 // keep current ± 6 frames in memory
    const MAX_CONCURRENCY = 3 // parallel decodes
    const PREFETCH_FORWARD = 12 // bias ahead when scrubbing forward

    // State
    const bitmaps = new Map() // index -> ImageBitmap or HTMLImageElement
    const inFlight = new Map() // index -> { promise, abort }
    let desiredIndex = -1
    let lastDrawnIndex = -1
    let rafScheduled = false
    let destroyed = false
    let lastDesiredIndex = -1 // for direction

    function clamp01(x) {
        return x <= 0 ? 0 : x >= 1 ? 1 : x
    }
    const want = (idx) => idx >= 0 && idx < frames.length

    function nearestRenderable(desired, lastDrawn) {
        const dir = Math.sign(desired - lastDrawn) || 1
        if (dir > 0) {
            // Forward: try desired → down to lastDrawn+1
            for (let i = desired; i > lastDrawn; i--) {
                if (bitmaps.has(i)) return i
            }
        } else {
            // Backward: try desired → up to lastDrawn-1
            for (let i = desired; i < lastDrawn; i++) {
                if (bitmaps.has(i)) return i
            }
        }
        // As a fallback, allow redrawing lastDrawn if it's at least cached
        if (bitmaps.has(lastDrawn)) return lastDrawn
        return -1
    }

    // ---- Loading with abort + concurrency cap ----
    async function loadBitmap(index) {
        if (bitmaps.has(index)) return bitmaps.get(index)
        if (inFlight.has(index)) return inFlight.get(index).promise

        const src = frames[index]
        if (!src) return null

        // Decide if same-origin
        let sameOrigin = false
        try {
            const u = new URL(src, location.href)
            sameOrigin = u.origin === location.origin
        } catch {}

        if (sameOrigin) {
            // Same-origin: fastest path with fetch + ImageBitmap and proper abort
            const abort = new AbortController()
            const promise = (async () => {
                try {
                    const res = await fetch(src, {
                        signal: abort.signal,
                        cache: 'force-cache',
                    })
                    if (!res.ok) return null
                    const blob = await res.blob()
                    const bmp = await createImageBitmap(blob, {
                        imageOrientation: 'from-image',
                    })
                    if (!destroyed) bitmaps.set(index, bmp)
                    return bmp
                } catch (err) {
                    return null
                } finally {
                    inFlight.delete(index)
                }
            })()
            inFlight.set(index, { promise, abort })
            pumpQueue()
            return promise
        }

        // Cross-origin: use HTMLImageElement to avoid CORS fetch errors.
        // Note: drawing will taint the canvas if the server does not send CORS headers,
        // which is fine if we never read pixels/export.
        const img = new Image()
        // If your CDN sends CORS headers, uncomment the next line:
        // img.crossOrigin = 'anonymous'
        img.decoding = 'async'

        let resolveFn, rejectFn
        const promise = new Promise((resolve, reject) => {
            resolveFn = resolve
            rejectFn = reject
        })

        function cleanup() {
            img.onload = null
            img.onerror = null
            inFlight.delete(index)
        }

        img.onload = async () => {
            cleanup()
            if (destroyed) return resolveFn(null)
            // Do NOT createImageBitmap here to avoid CORS taint issues; draw the <img> directly
            bitmaps.set(index, img)
            resolveFn(img)
        }

        img.onerror = () => {
            cleanup()
            resolveFn(null)
        }

        // Start request after listeners are set
        img.src = src

        const abort = {
            abort() {
                try {
                    img.onload = null
                    img.onerror = null
                    // cancel load
                    img.src = ''
                } catch {}
            },
        }

        inFlight.set(index, { promise, abort })
        pumpQueue()
        return promise
    }

    function abortIndex(index) {
        const entry = inFlight.get(index)
        if (entry) {
            try {
                entry.abort.abort()
            } catch {}
            inFlight.delete(index)
        }
    }

    function evictOutsideWindow(center) {
        const min = center - WINDOW_RADIUS
        const max = center + WINDOW_RADIUS
        for (const [i, bmp] of bitmaps) {
            if (i < min || i > max) {
                if (bmp && typeof bmp.close === 'function') bmp.close()
                bitmaps.delete(i)
            }
        }
        // Also abort any loads outside the window
        for (const i of [...inFlight.keys()]) {
            if (i < min || i > max) abortIndex(i)
        }
    }

    // Simple loader queue: keep starting loads until concurrency is hit
    function pumpQueue() {
        if (destroyed) return
        const running = inFlight.size
        if (running >= MAX_CONCURRENCY) return

        const dir = Math.sign(desiredIndex - lastDesiredIndex) || 1 // assume forward initially
        const min = desiredIndex - WINDOW_RADIUS
        const max = desiredIndex + WINDOW_RADIUS

        // Build a prioritized list around desiredIndex, biased by direction
        const order = []
        // current
        if (want(desiredIndex)) order.push(desiredIndex)
        // then ahead in desired direction, then behind
        const aheadFirst = dir >= 0
        const rangeA = aheadFirst
            ? [desiredIndex + 1, max, +1]
            : [desiredIndex - 1, min, -1]
        const rangeB = aheadFirst
            ? [desiredIndex - 1, min, -1]
            : [desiredIndex + 1, max, +1]

        for (
            let i = rangeA[0];
            aheadFirst ? i <= rangeA[1] : i >= rangeA[1];
            i += rangeA[2]
        )
            order.push(i)
        for (
            let i = rangeB[0];
            aheadFirst ? i >= rangeB[1] : i <= rangeB[1];
            i += rangeB[2]
        )
            order.push(i)

        // Start loads up to concurrency
        for (const i of order) {
            if (!want(i)) continue
            if (bitmaps.has(i) || inFlight.has(i)) continue
            loadBitmap(i).catch(() => {})
            if (inFlight.size >= MAX_CONCURRENCY) break
        }
    }

    // ---- Drawing (order-safe) ----
    function scheduleDraw() {
        if (rafScheduled) return
        rafScheduled = true
        requestAnimationFrame(() => {
            rafScheduled = false
            if (destroyed) return

            // Draw the closest available frame toward the desired one to avoid sticking
            let drawIndexTarget = -1
            const exact = bitmaps.get(desiredIndex)
            if (exact) {
                drawIndexTarget = desiredIndex
            } else {
                drawIndexTarget = nearestRenderable(
                    desiredIndex,
                    Math.max(-1, lastDrawnIndex)
                )
            }

            if (drawIndexTarget === -1) {
                pumpQueue()
                scheduleDraw()
                return
            }
            if (lastDrawnIndex === drawIndexTarget) return

            const srcImg = bitmaps.get(drawIndexTarget)

            // white background (alpha:false would otherwise show black)
            ctx.fillStyle = '#fff'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(srcImg, 0, 0, canvas.width, canvas.height)

            lastDrawnIndex = drawIndexTarget
        })
    }

    return {
        setProgress(progress) {
            if (!frames?.length) return
            const p = clamp01(progress)
            const idx = Math.min(
                frames.length - 1,
                Math.max(0, Math.floor(p * (frames.length - 1)))
            )

            if (idx === desiredIndex) return

            lastDesiredIndex = desiredIndex
            desiredIndex = idx

            // If we jumped far, cancel outdated loads to free bandwidth for the new window
            const jump = Math.abs(desiredIndex - lastDesiredIndex)
            if (jump > WINDOW_RADIUS * 2) {
                evictOutsideWindow(desiredIndex)
            }

            // Shrink memory to a small window and cancel irrelevant loads
            evictOutsideWindow(desiredIndex)

            // Ensure we’re loading the current frame
            loadBitmap(desiredIndex).catch(() => {})

            // Bias a bit more forward when scrubbing forward
            if (desiredIndex > lastDesiredIndex) {
                for (let i = 1; i <= PREFETCH_FORWARD; i++) {
                    const j = desiredIndex + i
                    if (want(j) && !bitmaps.has(j) && !inFlight.has(j))
                        loadBitmap(j).catch(() => {})
                }
            }

            pumpQueue()
            scheduleDraw()
        },

        destroy() {
            destroyed = true
            for (const [i, entry] of inFlight) {
                try {
                    entry.abort.abort()
                } catch {}
            }
            inFlight.clear()
            for (const bmp of bitmaps.values()) {
                try {
                    if (bmp && typeof bmp.close === 'function') {
                        bmp.close()
                    }
                } catch {}
            }
            bitmaps.clear()
        },
    }
}
